import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "backdrop", "panel", "title", "message", "confirmButton"]
  static values = {
    title: { type: String, default: "Are you sure?" },
    message: { type: String, default: "" },
    confirmText: { type: String, default: "Confirm" },
    variant: { type: String, default: "danger" }
  }

  // Programmatic API: any controller can dispatch
  // `new CustomEvent("confirm-dialog:show", { detail: { title, message,
  //   confirmText, variant, onConfirm } })` on document and the dialog
  // will open. Use this in place of native `confirm()` so we get a
  // styled, accessible modal instead of the browser dialog.
  connect() {
    this._programmaticHandler = this._handleProgrammatic.bind(this)
    document.addEventListener("confirm-dialog:show", this._programmaticHandler)
  }

  disconnect() {
    document.removeEventListener("confirm-dialog:show", this._programmaticHandler)
  }

  _handleProgrammatic(event) {
    const { title, message, confirmText, variant, onConfirm } = event.detail || {}
    this._programmaticOnConfirm = typeof onConfirm === "function" ? onConfirm : null

    this.titleTarget.textContent = title || this.titleValue
    this.messageTarget.textContent = message || this.messageValue
    this.confirmButtonTarget.textContent = confirmText || this.confirmTextValue

    this.confirmButtonTarget.className = (variant || this.variantValue) === "danger"
      ? "px-4 py-2 text-sm font-semibold rounded-lg text-white bg-danger-600 hover:bg-danger-700 active:scale-[0.98] transition-colors cursor-pointer"
      : "px-4 py-2 text-sm font-semibold rounded-lg text-white bg-primary-600 hover:bg-primary-700 active:scale-[0.98] transition-colors cursor-pointer"

    this.dialogTarget.classList.remove("hidden")
    this.backdropTarget.classList.add("animate-fade-in")
    this.panelTarget.classList.add("animate-scale-in")
    requestAnimationFrame(() => this.confirmButtonTarget.focus())
  }

  show(event) {
    // Re-entrant click from confirm() — let the original action through.
    if (event.currentTarget.dataset.confirmDialogConfirmed === "true") {
      delete event.currentTarget.dataset.confirmDialogConfirmed
      return
    }

    event.preventDefault()
    this._pendingForm = event.target.closest("form")
    this._pendingLink = event.target.closest("a")
    // Track the clicked submit button so requestSubmit() can pass it as the
    // form's submitter — preserves the button's name/value for forms with
    // multiple intent buttons (e.g. session note "Save draft" vs "Sign").
    this._pendingSubmitter = event.target.closest("button[type='submit']") || event.currentTarget
    this._pendingTrigger = event.currentTarget

    const trigger = event.currentTarget
    const title = trigger.dataset.confirmTitle || this.titleValue
    const message = trigger.dataset.confirmMessage || this.messageValue
    const confirmText = trigger.dataset.confirmText || this.confirmTextValue
    const variant = trigger.dataset.confirmVariant || this.variantValue

    this.titleTarget.textContent = title
    this.messageTarget.textContent = message
    this.confirmButtonTarget.textContent = confirmText

    this.confirmButtonTarget.className = variant === "danger"
      ? "px-4 py-2 text-sm font-semibold rounded-lg text-white bg-danger-600 hover:bg-danger-700 active:scale-[0.98] transition-colors cursor-pointer"
      : "px-4 py-2 text-sm font-semibold rounded-lg text-white bg-primary-600 hover:bg-primary-700 active:scale-[0.98] transition-colors cursor-pointer"

    this.dialogTarget.classList.remove("hidden")
    this.backdropTarget.classList.add("animate-fade-in")
    this.panelTarget.classList.add("animate-scale-in")

    requestAnimationFrame(() => this.confirmButtonTarget.focus())
  }

  confirm() {
    this._close()
    if (this._programmaticOnConfirm) {
      const handler = this._programmaticOnConfirm
      this._programmaticOnConfirm = null
      handler()
    } else if (this._pendingForm) {
      const submitter = this._pendingSubmitter && this._pendingForm.contains(this._pendingSubmitter) ? this._pendingSubmitter : null
      this._pendingForm.requestSubmit(submitter)
    } else if (this._pendingLink) {
      // Mark the trigger so the re-fired click bypasses our show() handler
      // and runs the link's natural action (Turbo's data-turbo-method etc.).
      if (this._pendingTrigger) {
        this._pendingTrigger.dataset.confirmDialogConfirmed = "true"
      }
      this._pendingLink.click()
    }
  }

  cancel() {
    this._close()
    this._pendingForm = null
    this._pendingLink = null
    this._pendingSubmitter = null
    this._pendingTrigger = null
    this._programmaticOnConfirm = null
  }

  _close() {
    this.backdropTarget.classList.remove("animate-fade-in")
    this.backdropTarget.classList.add("animate-fade-out")
    this.panelTarget.classList.remove("animate-scale-in")
    this.panelTarget.classList.add("animate-scale-out")

    setTimeout(() => {
      this.dialogTarget.classList.add("hidden")
      this.backdropTarget.classList.remove("animate-fade-out")
      this.panelTarget.classList.remove("animate-scale-out")
    }, 200)
  }
}
